import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  FileText, Search, RefreshCw, ChevronLeft, ChevronRight,
  MapPin, DollarSign, Calendar, Building2, Inbox,
  CheckCircle2, XCircle, Clock, Loader2, AlertCircle, TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const STATUS_CONFIG = {
  OPEN:     { color: 'bg-blue-100 text-blue-800',    label: 'Açık' },
  CLOSED:   { color: 'bg-gray-100 text-gray-600',    label: 'Kapandı' },
  AWARDED:  { color: 'bg-emerald-100 text-emerald-800', label: 'Verildi' },
  DRAFT:    { color: 'bg-yellow-100 text-yellow-700', label: 'Taslak' },
  EXPIRED:  { color: 'bg-red-100 text-red-700',      label: 'Süresi Doldu' },
};

const UltraBriefs = () => {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [kpis, setKpis] = useState({ total: 0, open: 0, awarded: 0, proposals: 0 });

  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  const fetchBriefs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(`${API}/ultra/briefs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = res.data.items || [];
      setBriefs(items);
      setTotal(res.data.total || 0);
      setKpis({
        total: res.data.total || 0,
        open: items.filter(b => b.status === 'OPEN').length,
        awarded: items.filter(b => b.status === 'AWARDED').length,
        proposals: items.reduce((s, b) => s + (b.proposal_count || 0), 0),
      });
    } catch (e) {
      setError('Briefler yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchBriefs(); }, [fetchBriefs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const kpiCards = [
    { label: 'Toplam Brief', value: kpis.total, icon: FileText, color: 'bg-blue-50 text-blue-600' },
    { label: 'Açık Brief', value: kpis.open, icon: Inbox, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Kazanılan', value: kpis.awarded, icon: CheckCircle2, color: 'bg-purple-50 text-purple-600' },
    { label: 'Toplam Teklif', value: kpis.proposals, icon: TrendingUp, color: 'bg-amber-50 text-amber-600' },
  ];

  const isExpired = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Brief Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Platformdaki tüm sergi brief'leri</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchBriefs} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Yenile
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="border-0 shadow-sm" data-testid={`kpi-briefs-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{k.value}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl ${k.color}`}><Icon className="h-5 w-5" /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Fuar adı, şehir, ülke ara..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 bg-gray-50 border-gray-200"
                data-testid="briefs-search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="briefs-status-filter"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="OPEN">Açık</option>
              <option value="CLOSED">Kapandı</option>
              <option value="AWARDED">Verildi</option>
              <option value="DRAFT">Taslak</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex items-center gap-2">
          <XCircle className="h-4 w-4 shrink-0" />{error}
        </div>
      )}

      {/* Table + Detail Panel */}
      <div className={`grid gap-6 ${selectedBrief ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}>
        <Card className={`border-0 shadow-sm ${selectedBrief ? 'lg:col-span-2' : ''}`}>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-7 w-7 animate-spin text-blue-400" />
              </div>
            ) : briefs.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                <p className="font-medium">Brief bulunamadı</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="briefs-table">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3.5">Brief / Fuar</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Katılımcı</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Bütçe</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Teklifler</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Son Tarih</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Durum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {briefs.map(b => {
                      const statusCfg = STATUS_CONFIG[b.status] || STATUS_CONFIG.OPEN;
                      const expired = isExpired(b.deadline_at);
                      return (
                        <tr
                          key={b.id}
                          onClick={() => setSelectedBrief(selectedBrief?.id === b.id ? null : b)}
                          className={`hover:bg-gray-50/80 transition-colors cursor-pointer ${selectedBrief?.id === b.id ? 'bg-blue-50/50' : ''}`}
                          data-testid={`brief-row-${b.id}`}
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                                <FileText className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900 truncate max-w-[180px]">{b.event_name || '—'}</p>
                                <p className="text-xs text-gray-400 flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />{b.event_city}{b.event_country ? `, ${b.event_country}` : ''}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-600 flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                              <span className="truncate max-w-[120px]">{b.org_name || b.org_id}</span>
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {b.budget_max > 0 ? (
                              <span className="text-sm font-semibold text-gray-700">
                                {b.budget_currency} {b.budget_max?.toLocaleString()}
                              </span>
                            ) : <span className="text-gray-400 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`text-sm font-bold ${b.proposal_count > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                              {b.proposal_count}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`text-xs ${expired ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                              {b.deadline_at || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                              {statusCfg.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Panel */}
        {selectedBrief && (
          <Card className="border-0 shadow-sm h-fit" data-testid="brief-detail-panel">
            <CardHeader className="pb-3 border-b border-gray-50">
              <div className="flex items-start justify-between">
                <CardTitle className="text-base">{selectedBrief.event_name}</CardTitle>
                <button onClick={() => setSelectedBrief(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase">Brief Detayları</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><p className="text-xs text-gray-400">ID</p><p className="font-mono text-xs text-gray-700">{selectedBrief.id}</p></div>
                  <div><p className="text-xs text-gray-400">Durum</p>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${(STATUS_CONFIG[selectedBrief.status] || STATUS_CONFIG.OPEN).color}`}>
                      {(STATUS_CONFIG[selectedBrief.status] || STATUS_CONFIG.OPEN).label}
                    </span>
                  </div>
                  <div><p className="text-xs text-gray-400">Şehir</p><p className="text-gray-700">{selectedBrief.event_city || '—'}</p></div>
                  <div><p className="text-xs text-gray-400">Ülke</p><p className="text-gray-700">{selectedBrief.event_country || '—'}</p></div>
                  <div><p className="text-xs text-gray-400">Bütçe Maks.</p>
                    <p className="font-semibold text-gray-800">
                      {selectedBrief.budget_max > 0 ? `${selectedBrief.budget_currency} ${selectedBrief.budget_max?.toLocaleString()}` : '—'}
                    </p>
                  </div>
                  <div><p className="text-xs text-gray-400">Teklifler</p>
                    <p className="font-bold text-blue-600">{selectedBrief.proposal_count}</p>
                  </div>
                  <div className="col-span-2"><p className="text-xs text-gray-400">Katılımcı</p>
                    <p className="text-gray-700 truncate">{selectedBrief.org_name || selectedBrief.org_id}</p>
                  </div>
                  <div><p className="text-xs text-gray-400">Son Tarih</p><p className="text-gray-700">{selectedBrief.deadline_at || '—'}</p></div>
                  <div><p className="text-xs text-gray-400">Oluşturulma</p>
                    <p className="text-gray-700 text-xs">{selectedBrief.created_at ? new Date(selectedBrief.created_at).toLocaleDateString('tr-TR') : '—'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{total} brief'ten {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} gösteriliyor</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraBriefs;
